import React, { FC } from "react";

import { Button, SectionCard, Spinner } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";
import ViewsService from "@/services/ViewsService";

import { useHomeNavigation } from "@/hooks/useHomeNavigation";

import { Dot } from "@/components/atoms/Dot/Dot";

import { TView, TProject, TTask } from "@/types";

/** Whose tasks: a project's, or everything a view gathers. */
export type TLatestTasksScope =
	{ project: TProject; view?: never } | { view: TView; project?: never };

export type TLatestTasksProps = TLatestTasksScope & {
	/** How many, newest first. */
	limit?: number;
	className?: string;
};

/**
 * The newest tasks of a project or of a view, one row each: the
 * project's colour, the name, the day and hours. A row opens the task
 * on the home page (which reads it from the app state, on the period
 * its filters hold - /tasks/:id alone shows nothing); "All tasks" goes
 * to the home page filtered on the project, or on the view.
 */
export const LatestTasks: FC<TLatestTasksProps> = ({
	project,
	view,
	limit = 5,
	className,
}) => {
	const { toTask, toProject, toView } = useHomeNavigation();

	const scope = project ? "project" : "view";
	const owner = project ?? view;
	// A project's tasks open on the home page under the current view;
	// a view's under itself.
	const viewId = view?.id;

	const tasks = useQuery({
		queryKey: ["tasks", scope, owner.id, "latest", limit],
		queryFn: () =>
			(project
				? ProjectsService.tasksOfProject(project.id, {
						limit,
						sortType: "DESC",
					})
				: ViewsService.getViewTasks(view.id, {
						limit,
						sortType: "DESC",
					})
			).then((resp: { items: TTask[] }) => resp.items),
	});

	// The task on the home page, on the day it happened. "All tasks":
	// the home page filtered on the project, or the view itself.
	const openTask = (task: TTask) => toTask(task.id, { viewId });
	const openAll = () => (project ? toProject(project.id) : toView(viewId));

	const items = tasks.data ?? [];

	return (
		<SectionCard
			title={t("latestTasks.title")}
			className={className}
			data-testid="latest-tasks"
			actions={
				<Button size="sm" variant="ghost" onClick={openAll}>
					{t("latestTasks.all")}
				</Button>
			}
		>
			{tasks.isLoading && <Spinner className="mx-auto my-4" />}
			{!tasks.isLoading && items.length === 0 && (
				<div className="flex flex-col gap-1 py-2 text-sm">
					<span className="font-medium">
						{t("latestTasks.empty")}
					</span>
					<span className="text-neutral-500">
						{t(`latestTasks.emptyHint.${scope}`)}
					</span>
				</div>
			)}
			<ul className="flex flex-col divide-y divide-neutral-100">
				{items.map((task) => (
					<li
						key={task.id}
						className="flex items-center gap-3 py-2.5 text-sm"
						data-testid={`task-${task.id}`}
					>
						<Dot
							color={task.project?.color ?? owner.color}
							className="h-2 w-2 min-w-2"
						/>
						<button
							type="button"
							onClick={() => openTask(task)}
							className="min-w-0 grow truncate text-left text-neutral-800 hover:underline"
						>
							{task.name}
						</button>
						<span className="shrink-0 text-xs text-neutral-500">
							{format(new Date(task.startDate), "d MMM · HH:mm")}
							{task.endDate
								? ` → ${format(new Date(task.endDate), "HH:mm")}`
								: ""}
						</span>
					</li>
				))}
			</ul>
		</SectionCard>
	);
};
